import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Trophy, Swords, Zap, Eye, Radio } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2L PR 1 — Tonight in the City.
 *
 * A curated, narrative-first ticker.  Auto-refreshes every 30s.  Each card
 * speaks the city's voice tonight — never a schedule list.
 *
 * Sorted by narrative significance on the backend (live > finals > comebacks
 * > rivalry kickoffs > semis > spectator surges).
 *
 * Two layouts:
 *   variant="strip"   → horizontal scroller for embedding on /arenax + /rankings
 *   variant="stack"   → vertical list for the dedicated city page
 */
export default function ArenaxTonightInCity({
  city = 'Bengaluru',
  sport = 'Football',
  limit = 8,
  variant = 'strip',
  testId = 'arenax-tonight-in-city',
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchPulse = () => {
      const params = new URLSearchParams();
      if (city) params.set('city', city);
      if (sport) params.set('sport', sport);
      params.set('limit', String(limit));
      api.get(`/arenax/tonight-in-city?${params.toString()}`)
        .then(r => { if (isMounted) setItems(r.data?.items || []); })
        .catch(() => { if (isMounted) setItems([]); })
        .finally(() => { if (isMounted) setLoading(false); });
    };
    fetchPulse();
    const tid = setInterval(fetchPulse, 30000);
    return () => { isMounted = false; clearInterval(tid); };
  }, [city, sport, limit]);

  if (loading || items.length === 0) return null;

  return (
    <section className="rounded-2xl border border-amber-500/20 p-5"
      data-testid={testId}
      style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.07), rgba(244,63,94,0.04) 60%, var(--ax-card-bg-soft))' }}>
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-amber-300 animate-pulse" />
          <span className="text-[10px] tracking-[0.3em] text-amber-300 font-black uppercase">
            Tonight in {city}
          </span>
        </div>
        <span className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">
          live · refreshing
        </span>
      </header>

      {variant === 'strip' ? (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth snap-x"
          data-testid={`${testId}-strip`}>
          {items.map((it, idx) => (
            <PulseCard key={`${it.kind}-${it.match_id || it.tournament_id}-${idx}`}
              item={it} compact />
          ))}
        </div>
      ) : (
        <div className="space-y-3" data-testid={`${testId}-stack`}>
          {items.map((it, idx) => (
            <PulseCard key={`${it.kind}-${it.match_id || it.tournament_id}-${idx}`}
              item={it} compact={false} />
          ))}
        </div>
      )}
    </section>
  );
}

const KIND_META = {
  live: { Icon: Flame, accent: 'text-amber-200', border: 'border-amber-500/40',
            bg: 'linear-gradient(135deg, rgba(245,158,11,0.12), var(--ax-card-bg-soft))',
            label: 'LIVE' },
  final: { Icon: Trophy, accent: 'text-amber-200', border: 'border-amber-500/40',
            bg: 'linear-gradient(135deg, rgba(245,158,11,0.10), var(--ax-card-bg-soft))',
            label: 'FINAL' },
  semifinal: { Icon: Swords, accent: 'text-rose-200', border: 'border-rose-500/35',
                bg: 'linear-gradient(135deg, rgba(244,63,94,0.10), var(--ax-card-bg-soft))',
                label: 'SEMI' },
  rivalry_kickoff: { Icon: Flame, accent: 'text-rose-200', border: 'border-rose-500/35',
                       bg: 'linear-gradient(135deg, rgba(244,63,94,0.10), var(--ax-card-bg-soft))',
                       label: 'RIVALRY' },
  comeback: { Icon: Zap, accent: 'text-amber-100', border: 'border-amber-500/35',
                bg: 'linear-gradient(135deg, rgba(251,113,133,0.12), rgba(245,158,11,0.08), var(--ax-card-bg-soft))',
                label: 'COMEBACK' },
  spectator_surge: { Icon: Eye, accent: 'text-cyan-200', border: 'border-cyan-500/35',
                       bg: 'linear-gradient(135deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))',
                       label: 'WATCH' },
};

function PulseCard({ item, compact }) {
  const meta = KIND_META[item.kind] || KIND_META.live;
  const { Icon, accent, border, bg, label } = meta;
  return (
    <Link
      to={item.deeplink || '/arenax'}
      data-testid={`arenax-tonight-card-${item.kind}-${item.match_id || item.tournament_id || 'x'}`}
      className={`group block ${compact ? 'shrink-0 w-80 snap-start' : ''} rounded-xl border ${border} p-4 transition-all hover:translate-y-[-1px]`}
      style={{ background: bg }}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={`h-3.5 w-3.5 ${accent} ${item.kind === 'live' ? 'animate-pulse' : ''}`} />
        <span className={`text-[9px] uppercase tracking-[0.25em] font-black ${accent}`}>
          {label}
        </span>
        {item.viewer_count >= 3 && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-cyan-200/85">
            <Eye className="h-3 w-3" />
            <span className="tabular-nums">{item.viewer_count}</span>
          </span>
        )}
        {item.hours_to_go != null && item.hours_to_go > 0 && (
          <span className="ml-auto text-[9px] uppercase tracking-wider text-slate-500 font-bold">
            in {item.hours_to_go < 1 ? '<1h' : `${Math.round(item.hours_to_go)}h`}
          </span>
        )}
      </div>
      <p className="text-sm leading-snug text-slate-100 font-bold"
        data-testid={`arenax-tonight-card-narrative-${item.kind}-${item.match_id || item.tournament_id || 'x'}`}>
        {item.narrative}
      </p>
    </Link>
  );
}
